type Pending = { resolve: (hash: string) => void; reject: (err: Error) => void };

// A hash should take well under a second; 30s means the worker is wedged.
// Reject so computeETag falls back to inline hashing instead of the pending
// promise (and its pLimit upload slot) hanging forever.
const HASH_WORKER_TIMEOUT_MS = 30_000;

let worker: Worker | null = null;
let seq = 0;
const pending = new Map<number, Pending>();

function rejectAll(message: string): void {
  for (const p of pending.values()) p.reject(new Error(message));
  pending.clear();
}

function getWorker(): Worker {
  if (worker) return worker;
  const created = new Worker(new URL("./hash.worker.ts", import.meta.url));
  created.onmessage = (
    event: MessageEvent<{ id: number; hash?: string; error?: string }>
  ) => {
    const { id, hash, error } = event.data;
    const p = pending.get(id);
    if (!p) return;
    pending.delete(id);
    if (error) p.reject(new Error(error));
    else p.resolve(hash as string);
  };
  created.onerror = () => {
    rejectAll("hash worker crashed");
    created.terminate();
    worker = null;
  };
  worker = created;
  return created;
}

/** Hash a file off the main thread. Rejects if a Worker can't be used. */
export function hashViaWorker(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (typeof Worker === "undefined") {
      reject(new Error("Worker unavailable"));
      return;
    }
    let w: Worker;
    try {
      w = getWorker();
    } catch (err) {
      reject(err instanceof Error ? err : new Error("worker init failed"));
      return;
    }
    const id = ++seq;
    const timer = setTimeout(() => {
      if (pending.delete(id)) {
        reject(new Error("hash worker timeout"));
      }
    }, HASH_WORKER_TIMEOUT_MS);
    const clearTimer = () => clearTimeout(timer);
    pending.set(id, {
      resolve: (hash: string) => {
        clearTimer();
        resolve(hash);
      },
      reject: (err: Error) => {
        clearTimer();
        reject(err);
      },
    });
    try {
      w.postMessage({ id, file });
    } catch (err) {
      clearTimer();
      pending.delete(id);
      reject(err instanceof Error ? err : new Error("postMessage failed"));
    }
  });
}
